// n=8 élite — Extractores PUROS de series para los dos niveles de evolución del
// informe (ver docs/journey/diseno-informe-n8-elite.md §2.5). Sin React, sin BD:
// reciben filas ya leídas y devuelven series listas para graficar (recharts en
// web / react-native-svg en app).
//
//   A) Intra-periodo  → quick_notes del entrenador firmante en las ~8 sesiones.
//   B) Entre informes → medias de pilar (§5.1) y de categoría (§5.2) a lo largo
//      de los n=8, leídas del detailed_scores histórico (sin columnas de series).
//
// Huecos: un punto ausente se devuelve como null (la línea se parte,
// connectNulls=false). Gating de la evolución ENTRE informes: con <2 informes no
// hay líneas reales (lo decide el consumidor con canShowEvolution); la
// intra-periodo NO se gatea.

import { type Catalogo, type CategoryDef, type PillarDef } from './evaluationCatalog';
import { getCatalog, isUnsupportedCatalogVersionError } from './evaluationReportV2';

export const EVOLUTION_MIN_REPORTS = 2;

// La evolución ENTRE informes (pilares + subgráficos) solo tiene sentido con ≥2
// informes; con 1 se muestra el punto único / "disponible a partir del 2º".
export function canShowEvolution(reportCount: number): boolean {
  return reportCount >= EVOLUTION_MIN_REPORTS;
}

// ── BUG 19 · un borrador NO es un punto de la evolución ──────────────────────
// Un informe en borrador tiene todo a 0 (o a medias), así que como punto de la
// serie pinta un VALLE INVENTADO y además infla el «total de informes». No lo
// puede arreglar la RLS: el ENTRENADOR y DIRECCIÓN sí pueden leer el borrador —
// tienen que poder—, así que lo descarta quien dibuja.
//
// El filtro es ESTRICTO (`=== 'published'`) a propósito: si una superficie nueva
// se olvida de pedir la columna `status`, el gráfico se queda VACÍO, que se ve
// al instante. Con el filtro laxo (`!== 'draft'`) el valle volvería en silencio,
// que es justo el bug que estamos cerrando.
export function publishedForEvolution<T extends { status?: string | null }>(rows: T[]): T[] {
  return rows.filter((r) => r.status === 'published');
}

// ── Evolución entre informes ──────────────────────────────────────────────────
export interface EvalRow {
  period_start: string; // 'YYYY-MM-DD'
  report_number?: string | null;
  detailed_scores: unknown; // jsonb crudo
  /**
   * Con qué catálogo se puntuó esta fila. Lo trae quien lee, igual que
   * `status`: sin él no se puede saber si una serie está cruzando versiones.
   * Ausente = v2, que es lo único que existía antes de que esto se pidiera.
   */
  catalog_version?: number | null;
  // BUG 19: lo trae quien lee para poder descartar los borradores ANTES de
  // graficar (ver `publishedForEvolution`). Opcional porque los extractores de
  // abajo son puros y no lo miran: el descarte va en la frontera, no aquí.
  status?: string | null;
}
export interface PeriodAxis {
  key: string;
  label: string;
}
export interface EvolutionSeries {
  key: string;
  label: string;
  color: string;
  points: (number | null)[]; // alineado con periods; null = hueco
}
/**
 * Por qué una serie no se dibuja. Es lo que se le enseña a quien la mira: un
 * gráfico vacío sin explicación se lee como «no hay datos», que es falso.
 */
export type SeriesBloqueo =
  | { motivo: 'versiones_mezcladas'; versiones: number[] }
  | { motivo: 'catalogo_no_soportado'; versiones: number[] };

export interface EvolutionData {
  periods: PeriodAxis[];
  series: EvolutionSeries[];
  /** Presente = no hay líneas, y esto dice el motivo. */
  bloqueo?: SeriesBloqueo;
}

// UNA SERIE QUE CRUZA VERSIONES DE CATÁLOGO NO SE DIBUJA.
//
// Estas series recorren VARIAS evaluaciones a la vez, y lo hacían con el
// catálogo vigente para todas. Mientras hubo una sola versión daba lo mismo; en
// cuanto hay dos, es el gráfico que pone un «Técnico» de la versión vieja y uno
// de la nueva EN LA MISMA LÍNEA, con una pendiente que no significa nada porque
// las dos puntas miden cosas distintas. Y de aquí sale también el PDF, que se
// firma.
//
// No se resuelve promediando ni rellenando: se rechaza y se dice por qué.
function catalogoComun(evals: EvalRow[]): { catalogo: Catalogo | null; bloqueo?: SeriesBloqueo } {
  const versiones = [...new Set(evals.map((e) => e.catalog_version ?? 2))].sort((a, b) => a - b);
  if (versiones.length === 0) return { catalogo: null };
  if (versiones.length > 1) {
    return { catalogo: null, bloqueo: { motivo: 'versiones_mezcladas', versiones } };
  }
  try {
    return { catalogo: getCatalog(versiones[0]) };
  } catch (err) {
    if (!isUnsupportedCatalogVersionError(err)) throw err;
    return { catalogo: null, bloqueo: { motivo: 'catalogo_no_soportado', versiones } };
  }
}

/** El texto del bloqueo, en una frase. Lo comparten web, app y PDF. */
export function textoBloqueo(b: SeriesBloqueo): string {
  const vs = b.versiones.map((v) => `v${v}`).join(' y ');
  return b.motivo === 'versiones_mezcladas'
    ? `Estos informes se puntuaron con catálogos distintos (${vs}). Sus notas no son comparables entre sí, así que la evolución no se dibuja.`
    : `Estos informes se puntuaron con un catálogo que esta versión no sabe leer (${vs}).`;
}

function isObj(v: unknown): v is Record<string, unknown> {
  return typeof v === 'object' && v !== null;
}
function round1(n: number): number {
  return Number(n.toFixed(1));
}

// Lee la media de una categoría del jsonb crudo SIN rellenar defaults (devuelve
// null si falta) para que los huecos sean honestos. Fallback: recomputa de subs.
function rawCategoryScore(ds: unknown, pillarKey: string, cat: CategoryDef): number | null {
  if (!isObj(ds)) return null;
  const pillar = ds[pillarKey];
  if (!isObj(pillar)) return null;
  const cats = pillar.categories;
  if (!isObj(cats)) return null;
  const c = cats[cat.key];
  if (!isObj(c)) return null;
  if (typeof c.score === 'number') return c.score;
  const subsObj = c.subs;
  if (!isObj(subsObj)) return null;
  const vals = cat.subs
    .map((s) => {
      const sv = subsObj[s.key];
      return isObj(sv) && typeof sv.score === 'number' ? sv.score : null;
    })
    .filter((v): v is number => v !== null);
  return vals.length ? round1(vals.reduce((a, b) => a + b, 0) / vals.length) : null;
}

// Media de pilar del jsonb crudo; fallback a recomputar desde sus categorías.
function rawPillarScore(ds: unknown, pillar: PillarDef): number | null {
  if (!isObj(ds)) return null;
  const p = ds[pillar.key];
  if (isObj(p) && typeof p.score === 'number') return p.score;
  const vals = pillar.categories
    .map((c) => rawCategoryScore(ds, pillar.key, c))
    .filter((v): v is number => v !== null);
  return vals.length ? round1(vals.reduce((a, b) => a + b, 0) / vals.length) : null;
}

function sortByPeriod(evals: EvalRow[]): EvalRow[] {
  return [...evals].sort((a, b) =>
    a.period_start < b.period_start ? -1 : a.period_start > b.period_start ? 1 : 0
  );
}
function periodsOf(sorted: EvalRow[]): PeriodAxis[] {
  return sorted.map((e, i) => ({
    key: `${e.period_start}#${i}`,
    label: e.report_number || e.period_start,
  }));
}

// §5.1 — una línea por PILAR a lo largo de los informes, con los pilares del
// catálogo COMÚN a todos ellos.
export function extractPillarSeries(evals: EvalRow[]): EvolutionData {
  const sorted = sortByPeriod(evals);
  const { catalogo, bloqueo } = catalogoComun(sorted);
  if (!catalogo) return { periods: periodsOf(sorted), series: [], ...(bloqueo ? { bloqueo } : {}) };
  return {
    periods: periodsOf(sorted),
    series: catalogo.pilares.map((p) => ({
      key: p.key,
      label: p.label,
      color: p.color,
      points: sorted.map((e) => rawPillarScore(e.detailed_scores, p)),
    })),
  };
}

// §5.2 — una línea por CATEGORÍA del pilar dado (subgráfico) a lo largo de los
// informes. Color = matiz del color del pilar.
export function extractCategorySeries(evals: EvalRow[], pillarKey: string): EvolutionData {
  const sorted = sortByPeriod(evals);
  const periods = periodsOf(sorted);
  const { catalogo, bloqueo } = catalogoComun(sorted);
  if (!catalogo) return { periods, series: [], ...(bloqueo ? { bloqueo } : {}) };
  const pillar = catalogo.pilares.find((p: PillarDef) => p.key === pillarKey);
  if (!pillar) return { periods, series: [] };
  const n = pillar.categories.length;
  return {
    periods,
    series: pillar.categories.map((c, idx) => ({
      key: c.key,
      label: c.label,
      color: shadeOf(pillar.color, idx, n),
      points: sorted.map((e) => rawCategoryScore(e.detailed_scores, pillarKey, c)),
    })),
  };
}

// ── Evolución intra-periodo ───────────────────────────────────────────────────
// Filas de quick_notes YA acotadas (entrenador firmante + periodo del informe),
// p. ej.:  SELECT s.date, qn.rating FROM quick_notes qn JOIN sessions s ...
export interface QuickNoteRow {
  date: string; // 'YYYY-MM-DD' (fecha de la sesión)
  rating: number; // 1-10
}
export interface IntraPeriodPoint {
  date: string;
  rating: number;
}

// Ordena cronológicamente las notas rápidas del periodo (≤8 puntos). Si no hay
// notas, devuelve [] (el consumidor oculta la mini-gráfica).
export function extractIntraPeriod(rows: QuickNoteRow[]): IntraPeriodPoint[] {
  return rows
    .filter((r) => typeof r.rating === 'number')
    .map((r) => ({ date: r.date, rating: r.rating }))
    .sort((a, b) => (a.date < b.date ? -1 : a.date > b.date ? 1 : 0));
}

// ── Paleta: matices del color de un pilar para las N categorías del subgráfico ──
function shadeOf(baseHex: string, index: number, count: number): string {
  const ratio = count <= 1 ? 0 : (index / (count - 1)) * 0.55; // 0% → ~55% hacia blanco
  const { r, g, b } = hexToRgb(baseHex);
  const mix = (c: number): number => Math.round(c + (255 - c) * ratio);
  return rgbToHex(mix(r), mix(g), mix(b));
}
function hexToRgb(hex: string): { r: number; g: number; b: number } {
  const h = hex.replace('#', '');
  const full =
    h.length === 3
      ? h
          .split('')
          .map((c) => c + c)
          .join('')
      : h;
  const n = parseInt(full, 16);
  return { r: (n >> 16) & 255, g: (n >> 8) & 255, b: n & 255 };
}
function rgbToHex(r: number, g: number, b: number): string {
  const h = (c: number): string => c.toString(16).padStart(2, '0');
  return `#${h(r)}${h(g)}${h(b)}`;
}
